import { MetricEnum } from "reports/utils/metricTypes";
import { SummaryMetricType } from "../../SummaryMetricsRow";
import { BreakdownRowStyles } from "./metricAndPnLBreakdownStyles";
import { isNumber } from "lodash";
import styled, { css } from "styled-components/macro";
import {
  PercentBarStylesProps,
  PERCENT_BAR_HEIGHT_PX,
} from "../PnLChart/PercentBar";
import { lightGrey, skuBreakdownGrey } from "../../../../constants/colors";
import { PnlTimes } from "../PnLChart/pnlChartTypes";
import { useTranslation } from "react-i18next";
import ReportButton from "reports/components/Reports/Buttons/ReportButton";
import {
  Tooltip,
  useMediaQuery,
  IconButton,
  MenuItem,
  Menu,
} from "@mui/material";
import { BREAKPOINT_TABLET } from "reports/constants/constants";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { useShouldFetchFromAskr } from "../../useShouldFetchFromAskr";
import { MoreVert, OpenInNew } from "@mui/icons-material";
import { Dispatch, MouseEvent, SetStateAction, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { BREAKPOINT_DESKTOP, useBreakpoints } from "constants/constants";
import { ReportParams } from "reports/types";
import { cardBorderLightBackground } from "constants/colors";
import { useIs24Hours, useIsWeekly } from "../../reportUtils";
import { useSelector } from "store/useSelector";
import { ReportRangeLinksEnum } from "components/DataGetter/FlightConfig/FlightConfigTypes/ReportPageConfigType";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import OverallBarstackTooltipContent from "components/Dashboard/OverallBarstack/OverallBarstackTooltipContent";
import { RollupWithTagsAndCountsAndRange } from "types/types";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { PnlCategoriesEnum } from "types/enums";
import { MetricToPropsMapping } from "reports/utils/metricUtils";

const SummaryBarStyles = styled.div<PercentBarStylesProps>`
  background: ${props => props.$color};
  width: ${props => props.$width}%;
  height: ${PERCENT_BAR_HEIGHT_PX}px;
  border: 1px solid ${lightGrey};
  border-radius: ${props =>
    props.$roundLeft && props.$roundRight
      ? "3px"
      : props.$roundRight
        ? "0px 3px 3px 0px"
        : props.$roundLeft
          ? "3px 0px 0px 3px"
          : "0px"};
`;

const BreakdownRow = ({
  showMetricTitle,
  breakdownName,
  skusTitle = "",
  breakdownSummaryMetrics,
  breakdownPnlTimes,
  isSkuBreakdown = false,
  maxBreakdownHeaderLength,
  isExpanded = false,
  shouldExpand,
  shouldShowButterfly = true,
  reportLink,
  isFirstRow,
  isLastRow,
  breakdownRange,
  processId,
}: {
  showMetricTitle: boolean;
  breakdownName: string;
  skusTitle?: string;
  breakdownSummaryMetrics: SummaryMetricType[];
  breakdownPnlTimes: PnlTimes[];
  isSkuBreakdown?: boolean;
  maxBreakdownHeaderLength: number;
  isExpanded?: boolean;
  shouldExpand?: boolean;
  shouldShowButterfly?: boolean;
  reportLink?: string;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  breakdownRange: {
    ge: number;
    lt: number;
  };
  processId: string;
}) => {
  const { t } = useTranslation("EOSReport");

  const isTabletOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_TABLET}px)`
  );

  const metricsOtherThanCount = breakdownSummaryMetrics?.filter(
    ({ name }) => name !== MetricEnum.COUNT
  );

  // Checks if all other metrics are null
  // If so, count doesn't make sense to show
  const shouldCountBeDashes =
    metricsOtherThanCount?.length > 0
      ? metricsOtherThanCount?.every(
          ({ numericValue }) => numericValue === null
        )
      : false;

  const breakdownPnlTimesWithValidValue = breakdownPnlTimes?.filter(
    breakdown =>
      breakdown?.value && breakdown.value >= 0 && isNumber(breakdown?.value)
  );

  const shouldShowMetricTitle =
    showMetricTitle ||
    (!isSkuBreakdown && isExpanded) ||
    (isTabletOrSmaller && !shouldExpand);

  const percentWidthOfMetricColumns = getPercentWidthOfMetricColumns(
    breakdownSummaryMetrics
  );

  /** report link is redundant if we're already on this page */
  const isReportLinkRedundant = reportLink?.includes(window.location.pathname);

  const isAdminUser = useIsAdmin();
  const reportConfig = useReportConfig();
  const allSummaryMetricsRows = [
    ...(reportConfig?.eosReportMetricsConfig?.summaryMetricsInputList ?? []),
  ];
  const { shouldFetchFromAskr } = useShouldFetchFromAskr(true);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(menuBtnRef.current);
  };
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isTabletOrLarger } = useBreakpoints();

  const is24Hour = useIs24Hours();
  const isWeekly = useIsWeekly();
  const showReportLinks = reportConfig?.eosReportGeneralConfig?.showReportLinks;
  const showReportLinksSkuBreakdown =
    isSkuBreakdown &&
    (typeof showReportLinks === "boolean"
      ? showReportLinks
      : showReportLinks?.includes(
          isWeekly
            ? ReportRangeLinksEnum.weekSku
            : is24Hour
              ? ReportRangeLinksEnum.daySku
              : ReportRangeLinksEnum.shiftSku
        ));
  return (
    <BreakdownRowStyles
      $numMetrics={breakdownSummaryMetrics ? breakdownSummaryMetrics.length : 0}
      {...{
        $percentWidthOfMetricColumns: percentWidthOfMetricColumns,
        $maxBreakdownHeaderLength: maxBreakdownHeaderLength,
        $isSkuBreakdown: isSkuBreakdown,
        $isExpanded: isExpanded,
        $shouldShowButterfly: shouldShowButterfly,
        $isFirstRow: isFirstRow,
        $isLastRow: isLastRow,
      }}
      onClick={isSkuBreakdown ? handleClick : undefined}
      $customCss={
        showReportLinksSkuBreakdown
          ? css`
              @media (max-width: ${BREAKPOINT_TABLET}px) {
                &&&&&&&& {
                  .machineHeader .btnMenu {
                    width: fit-content;
                    left: -3px;
                    top: -${isSkuBreakdown ? 8 : 3}px;
                    margin: 0;
                  }
                }
              }
              @media (min-width: ${BREAKPOINT_DESKTOP}px) {
                [class*="MachinePnlChartStyles"] {
                  padding-left: 64px !important;
                  padding-right: 64px !important;
                }
                &&&&&&&& {
                  .machineHeader .btnMenu {
                    left: -48px !important;
                  }
                  padding-left: 56px;
                  width: calc(100% + 56px);
                  margin-left: -28px;
                }
              }

              &:hover {
                background: ${isSkuBreakdown
                  ? cardBorderLightBackground
                  : skuBreakdownGrey};
              }
            `
          : css``
      }
    >
      <div className="machineHeader">
        <div className="machineName">
          {breakdownName === skusTitle ? "" : breakdownName}
        </div>
        {skusTitle ? (
          <div
            className={`skusTitle${
              breakdownName === skusTitle ? " add-space-right" : ""
            }`}
          >
            {skusTitle}
          </div>
        ) : null}
        {showReportLinksSkuBreakdown ? (
          <>
            <IconButton
              ref={menuBtnRef}
              size={isTabletOrLarger ? "medium" : "small"}
              className="btnMenu"
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
            <MoreOptionsMenu
              {...{
                processId,
                anchorEl,
                setAnchorEl,
                breakdownName,
                breakdownRange,
                isSkuBreakdown,
              }}
            />
          </>
        ) : null}
      </div>
      <div className="summaryMetrics">
        {shouldShowMetricTitle && (
          <div className="metrics metricsTitle">
            {breakdownSummaryMetrics?.map(({ name, nameTranslated }) => {
              const nameDisplay = nameTranslated ?? name;
              return (
                <div key={name} className="metric">
                  <strong>
                    {t(`${nameDisplay === skusTitle ? "" : nameDisplay}`)}
                  </strong>
                </div>
              );
            })}
          </div>
        )}

        <div className="summaryMetricsBar">
          <div className="metrics">
            {breakdownSummaryMetrics?.map(metric => {
              const metricProps = MetricToPropsMapping[metric.name];
              const thisMetricConfig = allSummaryMetricsRows.find(
                m => m.name === metric.name
              );
              const dataSource = isAdminUser
                ? `${shouldFetchFromAskr ? "askr" : "midgard"}:pnlBreakdown:${
                    (metricProps ?? thisMetricConfig)?.dataDumpKey
                  }`
                : "";
              const valueAndUnit = getValueAndUnit(metric, shouldCountBeDashes);

              return (
                <Tooltip
                  key={metric.name}
                  title={isAdminUser ? dataSource : ""}
                >
                  <div className="metric">
                    {valueAndUnit.props.children[0] === skusTitle
                      ? ""
                      : valueAndUnit}
                  </div>
                </Tooltip>
              );
            })}
          </div>
          <div className="fullBar">
            {shouldShowButterfly
              ? (() => {
                  const rollupToUse: RollupWithTagsAndCountsAndRange =
                    getRollupFromBreakdownPnlTimes(
                      breakdownPnlTimesWithValidValue,
                      breakdownRange
                    );

                  return (
                    <HtmlTooltip
                      title={
                        <OverallBarstackTooltipContent
                          {...{
                            rollupDisplay: rollupToUse,
                            filterCategoriesInPercent: [
                              PnlCategoriesEnum.notScheduled,
                            ],
                          }}
                        />
                      }
                      openOnMouseover={true}
                      leaveDelay={100}
                      stickyOpen={true}
                      style={{ width: "100%" }}
                    >
                      <div className="breakdown">
                        {breakdownPnlTimesWithValidValue?.map(
                          ({ name, value, color }, index) => {
                            // If value is higher than 100%, just set to 100 so it doesn't span too far
                            return value ? (
                              <SummaryBarStyles
                                key={name}
                                $width={value > 100 ? 100 : value}
                                $color={color}
                                $hideBorder={false}
                                $roundLeft={index === 0}
                                $roundRight={
                                  index ===
                                  breakdownPnlTimesWithValidValue.length - 1
                                }
                              />
                            ) : null;
                          }
                        )}
                      </div>
                    </HtmlTooltip>
                  );
                })()
              : reportLink &&
                !isReportLinkRedundant && (
                  <div className="breakdown">
                    <ReportButton href={reportLink} />
                  </div>
                )}
          </div>
        </div>
      </div>
    </BreakdownRowStyles>
  );
};

export default BreakdownRow;

function MoreOptionsMenu({
  anchorEl,
  setAnchorEl,
  processId,
  breakdownName,
  breakdownRange,
  isSkuBreakdown,
}: {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
  processId?: string;
  breakdownName: string;
  breakdownRange: {
    ge: number;
    lt: number;
  };
  isSkuBreakdown: boolean;
}) {
  const open = Boolean(anchorEl);
  const allGroups = useSelector(state => state.legacy.allGroups);
  const { processGroupName } = useParams<ReportParams>();
  const thisGroup = allGroups.find(
    group => group.shortName === processGroupName
  );
  const processIdx = thisGroup?.processes?.findIndex(
    ({ id }) => id === processId
  );
  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const ckey = useCustomerKey();
  const { isTabletOrLarger } = useBreakpoints();
  const { search } = useLocation();
  const workScheduleId = new URLSearchParams(search).get("workSchedule");
  return (
    <>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "top",
          horizontal: isTabletOrLarger ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: isTabletOrLarger ? "right" : "left",
        }}
      >
        <MenuItem>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`/${ckey}/dashboard/${processGroupName}${
              processId ? `/${processIdx}` : ""
            }?${!processId ? "view=tl&" : ""}ge=${Math.round(
              breakdownRange.ge
            )}&lt=${Math.round(breakdownRange.lt)}${
              workScheduleId ? `&workSchedule=${workScheduleId}` : ""
            }${
              isSkuBreakdown ? `&sku=${encodeURIComponent(breakdownName)}` : ""
            }`}
            style={{ display: "flex", gap: "0.5em", alignItems: "center" }}
            onClick={e => e.stopPropagation()}
          >
            View Timeline
            <OpenInNew />
          </a>
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Use metric name or max number of chars to determine width of that column in the breakdown row
 * Since we are using name or max number of chars,
 * this will be uniform so the columns will line up from one row to the next
 *
 * @param {SummaryMetricType[]} breakdownSummaryMetrics
 * @return number[]
 */
function getPercentWidthOfMetricColumns(
  breakdownSummaryMetrics: SummaryMetricType[]
) {
  const totalLengthOfMetricTitles = breakdownSummaryMetrics?.reduce(
    (accum, curr) => {
      accum += Math.max(
        curr.name.length,
        curr?.maxNumChars ?? curr.name.length
      );
      return accum;
    },
    0
  );
  const percentWidthOfMetricTitles = breakdownSummaryMetrics?.map(
    ({ name, maxNumChars }) =>
      (Math.max(name.length, maxNumChars ?? name.length) /
        totalLengthOfMetricTitles) *
      100
  );
  return percentWidthOfMetricTitles;
}

function getValueAndUnit(
  { name, numericValue, unit, color, stringFormattedValue }: SummaryMetricType,
  shouldCountBeDashes: boolean
) {
  const isCountMetric = name === MetricEnum.COUNT;
  const shouldShowValueAsDashes = isCountMetric && shouldCountBeDashes;
  const displayValue =
    numericValue === null || shouldShowValueAsDashes ? "--" : numericValue;

  const displayUnit = numericValue !== null ? unit : "";

  return (
    <div
      style={{
        color: `${displayValue === "--" ? "black" : color}`,
      }}
    >
      {stringFormattedValue ?? displayValue}
      {displayUnit}
    </div>
  );
}

/** ingore `time`, use `value` from PnlTimes as percent */
function getRollupFromBreakdownPnlTimes(
  breakdownPnlTimesWithValidValue: PnlTimes[],
  breakdownRange: { ge: number; lt: number }
) {
  return {
    ...Object.entries(breakdownPnlTimesWithValidValue).reduce(
      (acc, [_, val]) => {
        return {
          ...acc,
          [val.name]: val.time,
          totalDurationMs: acc.totalDurationMs + (val.time ?? 0),
        };
      },
      {
        notScheduled: 0,
        unknown: 0,
        planned: 0,
        producing: 0,
        waiting: 0,
        unplanned: 0,
        uncollected: 0,
        producingLoss: 0,
        range: breakdownRange,
        tags: {
          unknown: {},
          planned: {},
          producing: {},
          waiting: {},
          unplanned: {},
          uncollected: {},
          notScheduled: {},
        },
        counts: {
          unknown: {},
          planned: {},
          producing: {},
          waiting: {},
          unplanned: {},
          uncollected: {},
          notScheduled: {},
        },
        totalDurationMs: 0,
        avgCycleTime: 0,
      } as RollupWithTagsAndCountsAndRange
    ),
  };
}
